import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { ChevronDown, ChevronUp, Search, Sparkles, Shield, Users, Coins, Crown } from "lucide-react";
import SeoHead from "../components/SeoHead";

/**
 * FAQ — public landing page targeting the high-converting spiritual
 * services search queries that drive the most organic traffic. Indexed
 * by Google, linked from the global footer.
 *
 * Keywords are woven into authoritative-sounding answers — never stuffed.
 * Each answer is honest copy a real seeker would find useful.
 */
export default function FAQ() {
  const [query, setQuery] = useState("");

  const filtered = (groups) => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (i) =>
            i.q.toLowerCase().includes(q) ||
            i.a.toLowerCase().includes(q)
        ),
      }))
      .filter((g) => g.items.length > 0);
  };

  const groups = filtered(FAQ_DATA);

  return (
    <Layout>
      <SeoHead
        title="Cauldron FAQ — Trusted Spiritual Practitioners, Verified Readings, Sacred Bookings"
        description="Answers to the most common questions about Cauldron — the global spiritual marketplace for verified Ifá babalawos, Santería priests, tarot readers, energy healers, mediums, love spell casters, and authentic spiritual workers. Online spiritual consultations, ancestor readings, chakra healing, and trusted spiritual guidance."
        keywords="spiritual advisor online, psychic reading online, tarot reading online, Ifá consultation, Babalawo near me, Santería priest, online Ifá reading, Orisha consultation, Palo Mayombe consultation, energy healing online, spiritual healer near me, spiritual cleansing, aura reading, chakra healing, spiritual guidance online, verified spiritual practitioners, authentic spiritual marketplace, trusted spiritual practitioner, spiritual booking platform"
      />
      <div className="max-w-3xl mx-auto space-y-8 pb-16" data-testid="faq-page">
        <div className="text-center space-y-3">
          <div className="label-mystic text-secondary">Knowledge Base</div>
          <h1 className="font-serif text-5xl sm:text-6xl" data-testid="faq-heading">
            Frequently Asked Questions
          </h1>
          <p className="text-foreground/75 text-base leading-relaxed max-w-xl mx-auto">
            Honest answers for seekers booking a trusted spiritual practitioner
            and for practitioners considering joining the Cauldron — the global
            marketplace for verified, authentic spiritual services.
          </p>
        </div>

        {/* Search */}
        <div className="parchment p-2 flex items-center gap-2">
          <Search size={16} className="ml-2 shrink-0" style={{ color: "#D4B58C" }} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search FAQs — try 'Ifá', 'tarot', 'refund', 'verification'…"
            className="flex-1 bg-transparent border-0 px-3 py-2 text-base text-foreground placeholder:text-foreground/40 focus:outline-none"
            data-testid="faq-search"
          />
        </div>

        {/* Groups */}
        {groups.length === 0 && (
          <div className="text-center py-10 text-foreground/55 text-sm">
            No FAQ matches "<span className="text-secondary">{query}</span>". Try a broader search term.
          </div>
        )}

        {groups.map((g) => (
          <div key={g.title} className="space-y-3" data-testid={`faq-group-${g.id}`}>
            <div className="flex items-center gap-2 mt-6">
              <g.icon size={14} className="text-secondary" strokeWidth={1.75} />
              <h2 className="label-mystic text-secondary">{g.title}</h2>
            </div>
            {g.items.map((item, idx) => (
              <FaqItem key={`${g.id}-${idx}`} q={item.q} a={item.a} testid={`faq-item-${g.id}-${idx}`} />
            ))}
          </div>
        ))}

        {/* CTA strip */}
        <div className="parchment p-6 text-center mt-10" data-testid="faq-cta">
          <div className="label-mystic text-secondary mb-2">Ready to begin?</div>
          <h2 className="font-serif text-3xl mb-3">Join the Cauldron today</h2>
          <p className="text-foreground/70 text-sm max-w-xl mx-auto mb-5">
            Free for seekers — paid for practitioners. Every reading is
            escrow-protected, every practitioner is hand-verified, every dollar
            you spend supports an authentic spiritual worker — not an algorithm.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/auth?mode=register&role=CLIENT"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-secondary text-secondary-foreground text-xs uppercase tracking-[0.18em] hover:bg-secondary/85"
              data-testid="faq-cta-seeker"
            >
              Begin as a Seeker
            </Link>
            <Link
              to="/auth?mode=register&role=PROVIDER"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-sm border border-secondary/50 text-secondary text-xs uppercase tracking-[0.18em] hover:bg-secondary/[0.08]"
              data-testid="faq-cta-practitioner"
            >
              Apply as a Practitioner
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function FaqItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="parchment overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-5 py-4 flex items-start justify-between gap-3 text-left hover:bg-secondary/[0.04] transition-colors"
        aria-expanded={open}
      >
        <span className="font-serif text-lg text-foreground">{q}</span>
        {open
          ? <ChevronUp size={16} className="mt-1 shrink-0" style={{ color: "#D4B58C" }} />
          : <ChevronDown size={16} className="mt-1 shrink-0" style={{ color: "#D4B58C" }} />
        }
      </button>
      {open && (
        <div className="px-5 pb-5 text-foreground/85 text-sm leading-relaxed" data-testid={`${testid}-answer`}>
          {a}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// FAQ CONTENT — keywords woven naturally into authoritative answers.
// Every keyword from the SEO brief is represented at least once.
// ──────────────────────────────────────────────────────────────────────
const FAQ_DATA = [
  {
    id: "general",
    title: "General — What is Cauldron?",
    icon: Sparkles,
    items: [
      {
        q: "What is Cauldron?",
        a: "Cauldron is a global spiritual marketplace and spiritual community app where seekers can book trusted spiritual practitioners — Ifá babalawos, Santería priests, tarot readers, mediums, energy healers, spiritual mentors, manifestation coaches, and authentic spiritual workers across every major tradition. Every practitioner is hand-verified before they can take a booking, so you never wonder whether the person on the other end of your psychic reading online is real or vetted.",
      },
      {
        q: "Is Cauldron a spiritual services marketplace or a community?",
        a: "Both. Cauldron is the only spiritual booking platform that pairs a curated marketplace of metaphysical services and occult services with a private spiritual community for seekers. You can browse and book a spiritual advisor online for an immediate consultation, then return to the Feed to discuss what came up with fellow seekers and the Elders who walk those traditions daily.",
      },
      {
        q: "How is Cauldron different from a free spiritual advisor app?",
        a: "Free apps optimize for volume — they let anyone calling themselves a tarot reader near me, psychic near me, or energy healer take your money. Cauldron optimizes for trust. Every practitioner is ID-verified, lineage-checked when applicable (Ifá, Lucumí, Palo Mayombe, Santería, Quimbanda), and reviewed by our Elders before they're allowed to charge for spiritual consultation. We onboard slowly on purpose. Quality over scale, always.",
      },
      {
        q: "Where in the world is Cauldron available?",
        a: "Cauldron is a global spiritual marketplace. Practitioners can sign up from anywhere; seekers can book from anywhere. Sessions happen over end-to-end-encrypted video, voice, or chat — so whether you're looking for a Babalawo near me in Lagos, a Tata Nganga near me in Havana, or a Santería priest near me in New York, the search is the same and the booking is the same.",
      },
    ],
  },
  {
    id: "for-seekers",
    title: "For Seekers — Booking & Readings",
    icon: Users,
    items: [
      {
        q: "What kinds of spiritual readings can I book?",
        a: "Cauldron's marketplace covers the full spectrum of trusted spiritual practitioners and authentic spiritual services: tarot reading online, psychic reading online, medium reading online, ancestor reading, ancestor communication, aura reading online, chakra healing online, energy healing online, spiritual cleansing service, spiritual blockage removal, road opening spiritual work, love problems spiritual advice, relationship reading, career tarot reading, court case spiritual help, money manifestation help, manifestation coaching, holistic healing, and divination services from across African, Afro-Caribbean, European, and Indigenous traditions.",
      },
      {
        q: "Do you offer online Ifá readings and Santería consultations?",
        a: "Yes. Cauldron is one of the few platforms with a verified directory of Ifá consultation, Babalawo near me services, online Ifá reading, Orisha consultation, Santeria reading, Santeria priest near me, Lucumi consultation, Isese Ifá consultation, and Orunmila reading. Each Ifá and Lucumí practitioner is vetted against their initiating lineage and Itá when documented — so your spiritual consultation Ifá is with a real initiated diviner, not a hobbyist.",
      },
      {
        q: "Can I get a Palo Mayombe or Quimbanda consultation?",
        a: "Yes. Verified Tata Nganga near me services for Palo Mayombe consultation and Palo reading are listed alongside Quimbanda consultation, Quimbanda practitioner, Exu consultation, and Pomba Gira consultation. These services require a lineage attestation from a verified Tata or Madrinha before the practitioner is allowed to charge. We take initiatory traditions seriously.",
      },
      {
        q: "How does payment work? Is my money safe?",
        a: "Every booking is escrow-protected. When you book a spiritual consultation, the funds are held by Stripe until the session is delivered. If a practitioner cancels or no-shows, you're refunded automatically. We charge 10% to the practitioner — never to you.",
      },
      {
        q: "Can I get a refund if the reading isn't what I expected?",
        a: "If a session is not delivered, you get a full refund automatically. If the session was delivered but the experience didn't meet your expectations, you can open a dispute within 7 days and Cauldron's review team will mediate. We side with seekers when a practitioner doesn't show up; we side with practitioners when a seeker tries to fish for free spiritual guidance online.",
      },
      {
        q: "Are sessions private?",
        a: "Yes. All video sessions run on encrypted Twilio infrastructure. Chat and voice are also encrypted in transit. Cauldron staff cannot read your private conversation with your spiritual advisor or spiritual mentor.",
      },
      {
        q: "What does a love spell caster or love spell expert actually do on Cauldron?",
        a: "Verified love spell caster and love spell expert practitioners on Cauldron offer relationship spiritual advice, candle work, and traditional petitions rooted in their own lineage — Hoodoo, Conjure, Brujería, Santería, etc. We do not allow any practitioner to promise a guaranteed outcome (no real spiritual worker would). Instead, you'll find honest love problems spiritual advice grounded in the practitioner's tradition.",
      },
      {
        q: "Can Cauldron help with spiritual protection or removing negative energy?",
        a: "Yes — many of our practitioners specialize in spiritual protection, spiritual cleansing, removing negative energy, bad luck spiritual help, and spiritual blockage removal. Some offer remote work; others book a session with you over video to guide you through the steps. Filter the marketplace by 'cleansing' or 'protection' to see practitioners for your specific need.",
      },
    ],
  },
  {
    id: "trust",
    title: "Trust & Verification",
    icon: Shield,
    items: [
      {
        q: "How do you verify spiritual practitioners?",
        a: "Every practitioner goes through a multi-layer trust ladder: (1) government ID + selfie, (2) phone verification, (3) business or temple documentation, (4) lineage verification for initiatory traditions (Ifá, Lucumí, Palo, Quimbanda, Santería, etc.), and (5) endorsements from existing Verified Initiates or Elders. The badge you see on a practitioner's profile reflects exactly which steps they've completed — full transparency.",
      },
      {
        q: "What does 'verified spiritual practitioner' actually mean on Cauldron?",
        a: "A verified spiritual practitioner on Cauldron has completed at least the first three trust steps: identity, phone, and a business or temple document review. They're not anonymous, they're not pseudonymous, and they're accountable. A trusted spiritual practitioner with the Temple tier has also been signed off by Cauldron Elders. An Elder has been on the platform for a year, holds endorsements from other Elders, and represents the highest authority of the spiritual marketplace.",
      },
      {
        q: "What if I have a bad experience with a practitioner?",
        a: "Open a dispute from your booking screen. Cauldron's moderation team — staffed by senior practitioners and licensed therapists — reviews every dispute within 48 hours. Practitioners with repeated unresolved complaints lose their badge and may be removed from the platform entirely.",
      },
    ],
  },
  {
    id: "for-practitioners",
    title: "For Practitioners — Joining & Growing",
    icon: Crown,
    items: [
      {
        q: "Why should I sign up to Cauldron as a practitioner?",
        a: "Cauldron is the only spiritual practitioner platform built end-to-end for the modern occult worker. We help you grow spiritual business, get more spiritual clients, and reach a global audience without the algorithmic chaos of mainstream social media. Tarot business growth, psychic marketing, spiritual advertising, and online marketplace for spiritual services — it's all one platform. You keep 90% of every booking.",
      },
      {
        q: "How much does it cost to be a practitioner on Cauldron?",
        a: "Practitioners pay $49.99/month for an unlimited listing on the spiritual worker directory, escrow-protected payouts, video infrastructure, marketing tools, and access to the practitioner community. Cauldron takes 10% of each completed booking. No hidden fees, no per-message charges, no boost-or-die monetization.",
      },
      {
        q: "How long does practitioner approval take?",
        a: "ID verification + phone is automatic and usually completes in under a minute. Lineage verification for initiatory traditions (Ifá, Lucumí, Palo, Santería, Quimbanda) takes 3-10 business days because a real human Elder reviews your documents. We don't rubber-stamp — that's the whole point.",
      },
      {
        q: "Can I get clients for my tarot business through Cauldron?",
        a: "Yes — Cauldron is purpose-built to get clients for tarot business, psychic, mediumship, energy healing, manifestation coaching, and spiritual coaching work. We surface you in the marketplace, in tradition-specific hub pages, in the Trending Elders block on the Welcome page seekers see when they first sign up, and via UTM-tracked share buttons on your profile. The more reviews and endorsements you accumulate, the higher you rise.",
      },
      {
        q: "Is Cauldron the right spiritual marketing platform for me?",
        a: "If you're an authentic, lineage-respecting practitioner who wants long-term spiritual practitioner platform growth and a trusted spiritual worker directory listing more than you want viral TikTok virality, yes. If you sell mass-produced 'love spells' with guaranteed outcomes, no — and we'll catch it during verification.",
      },
      {
        q: "Do you support sigil work, candle work, root work, conjure, and traditional services?",
        a: "Yes — Cauldron supports the full range of occult services, metaphysical services, and divination services as long as the practitioner is honest about what they offer. Tarot, astrology, Hoodoo, Reiki, Ifá, mediumship, mediumship development coaching, manifestation coaching, holistic healing, ancestor communication, road opening spiritual work — all welcome.",
      },
      {
        q: "How do I get paid as a practitioner?",
        a: "Payouts run through Stripe Connect. You can also withdraw to USDC via Cauldron's CAUL token system — which lets you cash out in crypto on the XRPL network. Two-day average payout for Stripe; near-instant for CAUL withdrawals.",
      },
    ],
  },
  {
    id: "caul",
    title: "CAUL Reward Token",
    icon: Coins,
    items: [
      {
        q: "What is CAUL?",
        a: "CAUL is Cauldron's reward token, issued on the XRPL (XRP Ledger) mainnet. Practitioners earn CAUL for reviews, referrals, completed sessions, and tier milestones. Ambassadors earn 100 CAUL per client signup and 500 CAUL per paid practitioner signup (plus 5% USD commission for 90 days).",
      },
      {
        q: "What can I do with CAUL?",
        a: "Spend CAUL on platform features (boosted listings, profile glow, promoted hub placement), top up your wallet to pay for services, or — for verified practitioners — cash it out to real USDC via the Stripe Bridge integration. CAUL is fully on-chain and the wallet system uses Xaman (XRPL's official mobile wallet) for self-custody.",
      },
      {
        q: "How is the CAUL price determined?",
        a: "The CAUL/XRP exchange rate is pulled live from the XRPL decentralized exchange (DEX) every 60 seconds. Top-ups through Xaman use the live DEX price plus a 5% spread; top-ups through Stripe use the same live price plus the spread. The ticker pill at the top of your Wallet and Marketplace pages shows the current rate.",
      },
    ],
  },
];
